import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import apiClient from "../utils/apiClient";

const PlotForm = ({
  isOpen,
  selectedPlot,
  handleClose,
  handleInputChange,
  handlePlotUpdate,
  handlePlotDelete,
  plotTypes,
  isMobile = false,
}) => {
  const [soilTypes, setSoilTypes] = useState([]);
  const [loadingSoilTypes, setLoadingSoilTypes] = useState(false);
  const [plotGroups, setPlotGroups] = useState([]);
  const [loadingPlotGroups, setLoadingPlotGroups] = useState(false);
  const [showNewPlotGroup, setShowNewPlotGroup] = useState(false);
  const [newPlotGroupName, setNewPlotGroupName] = useState("");
  const [newPlotGroupRotationType, setNewPlotGroupRotationType] = useState("three_year");
  const [creatingPlotGroup, setCreatingPlotGroup] = useState(false);

  // Get the selected plot type to determine which fields to show
  const selectedPlotType = plotTypes.find(pt => pt.plot_type_uuid === selectedPlot?.plot_type_uuid);

  // Determine which fields to show based on plot type configuration
  const getFieldVisibility = () => {
    if (!selectedPlotType) return {};
    
    const formFields = selectedPlotType.form_fields || [];
    
    return {
      showSoilType: formFields.includes('soil_type'),
      showDimensions: formFields.includes('dimensions'),
      showDescription: formFields.includes('description'),
      showShape: formFields.includes('shape'),
    };
  };

  const fieldVisibility = getFieldVisibility();

  useEffect(() => {
    const fetchSoilTypes = async () => {
      setLoadingSoilTypes(true);
      try {
        const response = await apiClient.get("/soiltypes");
        setSoilTypes(response.data);
      } catch (err) {
        console.error("Failed to fetch soil types:", err);
      } finally {
        setLoadingSoilTypes(false);
      }
    };

    const fetchPlotGroups = async () => {
      setLoadingPlotGroups(true);
      try {
        const response = await apiClient.get("/plot_groups");
        setPlotGroups(response.data);
      } catch (err) {
        console.error("Failed to fetch plot groups:", err);
      } finally {
        setLoadingPlotGroups(false);
      }
    };

    if (isOpen) {
      fetchSoilTypes();
      fetchPlotGroups();
      setShowNewPlotGroup(false);
      setNewPlotGroupName("");
      setNewPlotGroupRotationType("three_year");
    }
  }, [isOpen]);

  const handleCreatePlotGroup = async () => {
    if (!newPlotGroupName.trim()) {
      return;
    }

    setCreatingPlotGroup(true);
    try {
      const response = await apiClient.post("/plot_groups", {
        name: newPlotGroupName.trim(),
        rotation_type: newPlotGroupRotationType,
      });
      setPlotGroups([...plotGroups, response.data]);
      handleInputChange({
        target: { name: "plot_group_uuid", value: response.data.plot_group_uuid },
      });
      setNewPlotGroupName("");
      setNewPlotGroupRotationType("three_year");
      setShowNewPlotGroup(false);
    } catch (err) {
      console.error("Failed to create plot group:", err);
      alert(err.response?.data?.detail || "Failed to create plot group");
    } finally {
      setCreatingPlotGroup(false);
    }
  };

  const formContent = (
    <Box sx={{ padding: 2, width: isMobile ? 'auto' : 300 }}>
      <Typography variant="h5" gutterBottom>
        Edit Plot
      </Typography>
      {selectedPlot && (
        <>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={selectedPlot.name}
              onChange={handleInputChange}
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="plot-type-label">Plot Type</InputLabel>
              <Select
                labelId="plot-type-label"
                name="plot_type_uuid"
                value={selectedPlot.plot_type_uuid || ""}
                onChange={(e) =>
                  handleInputChange({ target: { name: "plot_type_uuid", value: e.target.value } })
                }
              >
                {plotTypes.map((plotType) => (
                  <MenuItem key={plotType.plot_type_uuid} value={plotType.plot_type_uuid}>
                    {plotType.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {fieldVisibility.showShape && (
              <FormControl fullWidth margin="normal">
                <InputLabel id="shape-label">Shape</InputLabel>
                <Select
                  labelId="shape-label"
                  name="shape"
                  value={selectedPlot.shape || "rectangle"}
                  onChange={(e) =>
                    handleInputChange({ target: { name: "shape", value: e.target.value } })
                  }
                >
                  <MenuItem value="rectangle">Rectangle</MenuItem>
                  <MenuItem value="circle">Circle</MenuItem>
                  <MenuItem value="triangle">Triangle</MenuItem>
                </Select>
              </FormControl>
            )}
            {fieldVisibility.showDescription && (
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={selectedPlot.description}
                onChange={handleInputChange}
                margin="normal"
              />
            )}
            
            {fieldVisibility.showSoilType && (
              <FormControl fullWidth margin="normal" disabled={loadingSoilTypes}>
                <InputLabel id="soil-type-label">Soil Type</InputLabel>
                <Select
                  labelId="soil-type-label"
                  name="soil_type_uuid"
                  value={selectedPlot.soil_type_uuid || ""}
                  onChange={(e) =>
                    handleInputChange({
                      target: { name: "soil_type_uuid", value: e.target.value },
                    })
                  }
                >
                  {loadingSoilTypes ? (
                    <MenuItem value="">
                      <CircularProgress size={20} />
                    </MenuItem>
                  ) : (
                    soilTypes.map((soil) => (
                      <MenuItem key={soil.soil_type_uuid} value={soil.soil_type_uuid}>
                        {soil.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}
            
            {fieldVisibility.showDimensions && (
              <>
                <TextField
                  fullWidth
                  label="Width"
                  name="width"
                  type="number"
                  value={selectedPlot.width}
                  onChange={handleInputChange}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Depth"
                  name="depth"
                  type="number"
                  value={selectedPlot.depth}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </>
            )}
            {selectedPlot.shape === "triangle" && (
              <TextField
                fullWidth
                label="Rotation (degrees)"
                name="rotation"
                type="number"
                value={selectedPlot.rotation || 0}
                onChange={handleInputChange}
                inputProps={{ min: 0, max: 360, step: 1 }}
                margin="normal"
              />
            )}
            <FormControl fullWidth margin="normal">
              <InputLabel id="plot-group-label">Plot Group</InputLabel>
              <Select
                labelId="plot-group-label"
                name="plot_group_uuid"
                value={selectedPlot.plot_group_uuid || ""}
                onChange={(e) =>
                  handleInputChange({
                    target: { name: "plot_group_uuid", value: e.target.value || null },
                  })
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {loadingPlotGroups ? (
                  <MenuItem value="">
                    <CircularProgress size={20} />
                  </MenuItem>
                ) : (
                  plotGroups.map((pg) => (
                    <MenuItem key={pg.plot_group_uuid} value={pg.plot_group_uuid}>
                      {pg.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <Box sx={{ marginTop: 1, marginBottom: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowNewPlotGroup(!showNewPlotGroup)}
                fullWidth
              >
                {showNewPlotGroup ? "Cancel" : "+ Add New Plot Group"}
              </Button>
            </Box>
            <Collapse in={showNewPlotGroup}>
              <Box sx={{ padding: 2, backgroundColor: "rgba(0, 0, 0, 0.02)", borderRadius: 1, marginBottom: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Create New Plot Group
                </Typography>
                <TextField
                  fullWidth
                  label="Name"
                  value={newPlotGroupName}
                  onChange={(e) => setNewPlotGroupName(e.target.value)}
                  margin="normal"
                  size="small"
                  disabled={creatingPlotGroup}
                />
                <FormControl fullWidth margin="normal" size="small">
                  <InputLabel id="rotation-type-label">Rotation Type</InputLabel>
                  <Select
                    labelId="rotation-type-label"
                    id="rotation-type-select"
                    value={newPlotGroupRotationType}
                    label="Rotation Type"
                    onChange={(e) => setNewPlotGroupRotationType(e.target.value)}
                    disabled={creatingPlotGroup}
                  >
                    <MenuItem value="three_year">3 Year</MenuItem>
                    <MenuItem value="four_year">4 Year</MenuItem>
                    <MenuItem value="six_year">6 Year</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleCreatePlotGroup}
                  disabled={!newPlotGroupName.trim() || creatingPlotGroup}
                  fullWidth
                  sx={{ marginTop: 1 }}
                >
                  {creatingPlotGroup ? <CircularProgress size={20} /> : "Create"}
                </Button>
              </Box>
            </Collapse>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedPlot?.locked || false}
                  onChange={(e) =>
                    handleInputChange({ target: { name: "locked", value: e.target.checked } })
                  }
                />
              }
              label="Lock Plot"
            />
            <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
              <Button variant="contained" color="primary" onClick={handlePlotUpdate}>
                Save
              </Button>
              <Button variant="contained" color="error" onClick={handlePlotDelete}>
                Delete
              </Button>
            </Box>
        </>
      )}
    </Box>
  );

  // Use Dialog on mobile, Drawer on desktop
  if (isMobile) {
    return (
      <Dialog
        open={isOpen}
        onClose={handleClose}
        fullScreen
        className="plot-form-dialog-mobile"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Edit Plot</Typography>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPlot && (
            <>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={selectedPlot.name}
                onChange={handleInputChange}
                margin="normal"
              />
              <FormControl fullWidth margin="normal">
                <InputLabel id="plot-type-label">Plot Type</InputLabel>
                <Select
                  labelId="plot-type-label"
                  name="plot_type_uuid"
                  value={selectedPlot.plot_type_uuid || ""}
                  onChange={(e) =>
                    handleInputChange({ target: { name: "plot_type_uuid", value: e.target.value } })
                  }
                >
                  {plotTypes.map((plotType) => (
                    <MenuItem key={plotType.plot_type_uuid} value={plotType.plot_type_uuid}>
                      {plotType.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {fieldVisibility.showShape && (
                <FormControl fullWidth margin="normal">
                  <InputLabel id="shape-label">Shape</InputLabel>
                  <Select
                    labelId="shape-label"
                    name="shape"
                    value={selectedPlot.shape || "rectangle"}
                    onChange={(e) =>
                      handleInputChange({ target: { name: "shape", value: e.target.value } })
                    }
                  >
                    <MenuItem value="rectangle">Rectangle</MenuItem>
                    <MenuItem value="circle">Circle</MenuItem>
                    <MenuItem value="triangle">Triangle</MenuItem>
                  </Select>
                </FormControl>
              )}
              {fieldVisibility.showDescription && (
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={selectedPlot.description}
                  onChange={handleInputChange}
                  margin="normal"
                />
              )}
              {fieldVisibility.showSoilType && (
                <FormControl fullWidth margin="normal" disabled={loadingSoilTypes}>
                  <InputLabel id="soil-type-label">Soil Type</InputLabel>
                  <Select
                    labelId="soil-type-label"
                    name="soil_type_uuid"
                    value={selectedPlot.soil_type_uuid || ""}
                    onChange={(e) =>
                      handleInputChange({
                        target: { name: "soil_type_uuid", value: e.target.value },
                      })
                    }
                  >
                    {loadingSoilTypes ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      soilTypes.map((soil) => (
                        <MenuItem key={soil.soil_type_uuid} value={soil.soil_type_uuid}>
                          {soil.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              )}
              {fieldVisibility.showDimensions && (
                <>
                  <TextField
                    fullWidth
                    label="Width"
                    name="width"
                    type="number"
                    value={selectedPlot.width}
                    onChange={handleInputChange}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Depth"
                    name="depth"
                    type="number"
                    value={selectedPlot.depth}
                    onChange={handleInputChange}
                    margin="normal"
                  />
                </>
              )}
              {selectedPlot.shape === "triangle" && (
                <TextField
                  fullWidth
                  label="Rotation (degrees)"
                  name="rotation"
                  type="number"
                  value={selectedPlot.rotation || 0}
                  onChange={handleInputChange}
                  inputProps={{ min: 0, max: 360, step: 1 }}
                  margin="normal"
                />
              )}
              <FormControl fullWidth margin="normal">
                <InputLabel id="plot-group-label">Plot Group</InputLabel>
                <Select
                  labelId="plot-group-label"
                  name="plot_group_uuid"
                  value={selectedPlot.plot_group_uuid || ""}
                  onChange={(e) =>
                    handleInputChange({
                      target: { name: "plot_group_uuid", value: e.target.value || null },
                    })
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {loadingPlotGroups ? (
                    <MenuItem value="">
                      <CircularProgress size={20} />
                    </MenuItem>
                  ) : (
                    plotGroups.map((pg) => (
                      <MenuItem key={pg.plot_group_uuid} value={pg.plot_group_uuid}>
                        {pg.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              <Box sx={{ marginTop: 1, marginBottom: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowNewPlotGroup(!showNewPlotGroup)}
                  fullWidth
                >
                  {showNewPlotGroup ? "Cancel" : "+ Add New Plot Group"}
                </Button>
              </Box>
              <Collapse in={showNewPlotGroup}>
                <Box sx={{ padding: 2, backgroundColor: "rgba(0, 0, 0, 0.02)", borderRadius: 1, marginBottom: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Create New Plot Group
                  </Typography>
                  <TextField
                    fullWidth
                    label="Name"
                    value={newPlotGroupName}
                    onChange={(e) => setNewPlotGroupName(e.target.value)}
                    margin="normal"
                    size="small"
                    disabled={creatingPlotGroup}
                  />
                  <FormControl fullWidth margin="normal" size="small">
                    <InputLabel id="rotation-type-label">Rotation Type</InputLabel>
                    <Select
                      labelId="rotation-type-label"
                      id="rotation-type-select"
                      value={newPlotGroupRotationType}
                      label="Rotation Type"
                      onChange={(e) => setNewPlotGroupRotationType(e.target.value)}
                      disabled={creatingPlotGroup}
                    >
                      <MenuItem value="three_year">3 Year</MenuItem>
                      <MenuItem value="four_year">4 Year</MenuItem>
                      <MenuItem value="six_year">6 Year</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCreatePlotGroup}
                    disabled={!newPlotGroupName.trim() || creatingPlotGroup}
                    fullWidth
                    sx={{ marginTop: 1 }}
                  >
                    {creatingPlotGroup ? <CircularProgress size={20} /> : "Create"}
                  </Button>
                </Box>
              </Collapse>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedPlot?.locked || false}
                    onChange={(e) =>
                      handleInputChange({ target: { name: "locked", value: e.target.checked } })
                    }
                  />
                }
                label="Lock Plot"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {selectedPlot && (
            <>
              <Button variant="contained" color="error" onClick={handlePlotDelete} fullWidth>
                Delete
              </Button>
              <Button variant="contained" color="primary" onClick={handlePlotUpdate} fullWidth>
                Save
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  // Desktop: Use Drawer
  return (
    <Drawer anchor="right" open={isOpen} onClose={handleClose}>
      {formContent}
    </Drawer>
  );
};

export default PlotForm;
